import { AfterContentInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { User, UserInput, OrganizationInput } from '@app/models';
import { DateService, ErrorHandlerService, UserService } from '@app/services';
import { TranslateService } from '@ngx-translate/core';

export type ModalResult = void;
export interface ModalPublicProperties {
  user: User;
}

@Component({
  selector: 'app-edit-profile-modal',
  templateUrl: './edit-profile-modal.component.html',
  styleUrls: ['./edit-profile-modal.component.scss'],
  standalone: false
})
export class EditProfileModalComponent implements ModalPublicProperties, AfterContentInit {
  @Input() user: User;
  form!: UntypedFormGroup;
  isLoading = false;
  isCompany = false;
  isCpfRequired = false;
  isBirthdateRequired = false;
  @ViewChild('nameInput', { static: false }) private nameInput: ElementRef<HTMLInputElement>;
  @ViewChild('companyInput', { static: false }) private companyInput: ElementRef<HTMLInputElement>;

  constructor(
    public modal: NgbActiveModal,
    public translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private userService: UserService,
    private dateService: DateService
  ) {
    this.form = this.formBuilder.group({
      user: this.formBuilder.group({
        name: '',
        cpf: '',
        birthday: '',
        organization: this.formBuilder.group({ name: '', cnpj: '' } as OrganizationInput)
      })
    } as UserInput);
  }

  ngAfterContentInit() {
    this.form.setValue({
      user: {
        name: this.user.name,
        cpf: this.user.cpf,
        birthday: this.dateService.normalizeDate(this.user.birthday),
        organization: {
          name: this.form.get('user.organization.name').value || this.user.company || null,
          cnpj: this.form.get('user.organization.cnpj').value || this.user.cnpj || null
        }
      }
    } as UserInput);
    this.isCompany = !!this.form.get('user.organization.name').value;
    this.isCpfRequired = !!this.user.cpf;
    this.isBirthdateRequired = !!this.user.birthday;
    this.form.markAllAsTouched();
    this.setInputFocus();
  }

  updateProfile() {
    this.isLoading = true;
    this.form.markAllAsTouched();

    const userParams = this.form.value;
    if (userParams.user && userParams.user.organization) {
      userParams.user.organization.name = (this.isCompany ? userParams.user.organization.name : null) || null;
      userParams.user.organization.cnpj = (this.isCompany ? userParams.user.organization.cnpj : null) || null;
    }
    userParams.user.birthday = this.dateService.normalizeDate(userParams.user.birthday);

    this.userService
      .updateCurrentUser(userParams)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => this.modal.close(),
        error: error => this.errorHandlerService.handleValidation(this.form, error)
      });
  }

  setInputFocus() {
    setTimeout(() => (this.isCompany ? this.companyInput : this.nameInput).nativeElement.focus());
  }
}
